'use client';

import { useState, useEffect, useRef } from 'react';
import { Drawer } from '../../../../common/components/Drawer/Drawer';
import {
  DrawerComponent,
  useDrawerMetadata,
  useModalDrawerContext,
} from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { WithdrawalSummaryDrawer } from '../WithdrawalSummary/WithdrawalSummaryDrawer';
import { WithdrawalAmountContent } from './WithdrawalAmountContent';
import { WithdrawalAmountSkeleton } from './WithdrawalAmountSkeleton';
import { getFiatWalletWithAccountInfo } from '../../../../common/gqlActions/getFiatWalletWithAccountInfo.action';
import { useAppNotification } from '@bts-web/core-features/notifications';
import { useClientTranslation } from '@bts-web/utils-lokalise';

interface WithdrawalAmountDrawerProps {
  initialInputAmount?: string;
  fromSell?: boolean;
}

type FiatWalletInfo = {
  withdrawalBalance: string;
  walletAmount: string;
  unconfirmedBalance: string;
};

const MINIMUM_WITHDRAWAL_AMOUNT = '1';

const WithdrawalAmountDrawer: DrawerComponent<WithdrawalAmountDrawerProps> = ({
  initialInputAmount,
  fromSell,
}) => {
  const { closeThisDrawer } = useDrawerMetadata();

  const { openDrawer } = useModalDrawerContext();

  const { t } = useClientTranslation();

  const { setAppNotification } = useAppNotification();

  // Using this ref to avoid double toast trigger
  const notifiedRef = useRef(false);

  const [availableBalances, setAvailableBalances] =
    useState<FiatWalletInfo | null>(null);

  useEffect(() => {
    if (fromSell && !notifiedRef.current) {
      notifiedRef.current = true;

      setAppNotification({
        visual: 'info',
        title: t('fiat_withdrawal_after_sell_title'),
        subtitle: t('fiat_withdrawal_after_sell_description'),
      });
    }

    getFiatWalletWithAccountInfo()
      .then((res) =>
        setAvailableBalances({
          withdrawalBalance: res.data.withdrawalBalance,
          walletAmount: res.data.walletAmount,
          unconfirmedBalance: res.data.unconfirmedBalance,
        }),
      )
      .catch(() => closeThisDrawer());
  }, []);

  const handleConfirm = (amount: string) => {
    closeThisDrawer();

    openDrawer(WithdrawalSummaryDrawer, { amount });
  };

  return (
    <Drawer hideDefaultHeader>
      {availableBalances ? (
        <WithdrawalAmountContent
          withdrawalBalance={availableBalances.withdrawalBalance}
          walletAmount={availableBalances.walletAmount}
          unconfirmedBalance={availableBalances.unconfirmedBalance}
          minimumAmount={MINIMUM_WITHDRAWAL_AMOUNT}
          initialAmount={initialInputAmount}
          onConfirm={handleConfirm}
        />
      ) : (
        <WithdrawalAmountSkeleton />
      )}
    </Drawer>
  );
};

export { WithdrawalAmountDrawer };

export type { WithdrawalAmountDrawerProps };
